import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { Tabs as TabsPrimitive } from 'radix-ui';

import { cn } from '@/lib/utils';

function Tabs({
    className,
    orientation = 'horizontal',
    ...props
}: React.ComponentProps<typeof TabsPrimitive.Root>) {
    return (
        <TabsPrimitive.Root
            data-slot="tabs"
            data-orientation={orientation}
            className={cn('group/tabs flex gap-5 data-horizontal:flex-col', className)}
            {...props}
        />
    );
}

const tabsListVariants = cva(
    'group/tabs-list items-center text-text-secondary group-data-vertical/tabs:h-fit group-data-vertical/tabs:flex-col',
    {
        variants: {
            variant: {
                default:
                    'inline-flex w-fit justify-center gap-1 rounded-lg border border-border-default bg-neutral-100 p-1 dark:bg-bg-raised',
                line: 'w-full gap-0 rounded-none border-b border-border-default bg-transparent p-0',
            },
        },
        defaultVariants: {
            variant: 'default',
        },
    },
);

function TabsList({
    className,
    variant = 'default',
    ...props
}: React.ComponentProps<typeof TabsPrimitive.List> & VariantProps<typeof tabsListVariants>) {
    return (
        <TabsPrimitive.List
            data-slot="tabs-list"
            data-variant={variant}
            className={cn(tabsListVariants({ variant }), className)}
            {...props}
        />
    );
}

function TabsTrigger({ className, ...props }: React.ComponentProps<typeof TabsPrimitive.Trigger>) {
    return (
        <TabsPrimitive.Trigger
            data-slot="tabs-trigger"
            className={cn(
                "relative inline-flex h-full flex-1 items-center justify-center gap-1.5 px-4 py-1 text-sm font-medium whitespace-nowrap text-text-secondary transition-all duration-150 group-data-vertical/tabs:w-full group-data-vertical/tabs:justify-start focus-visible:ring-3 focus-visible:ring-primary-200 focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
                // Pill variant (default)
                'group-data-[variant=default]/tabs-list:rounded-md group-data-[variant=default]/tabs-list:hover:text-text-primary group-data-[variant=default]/tabs-list:data-active:bg-bg-surface group-data-[variant=default]/tabs-list:data-active:text-text-heading group-data-[variant=default]/tabs-list:data-active:shadow-xs',
                // Line variant
                'group-data-[variant=line]/tabs-list:rounded-none group-data-[variant=line]/tabs-list:border-b-2 group-data-[variant=line]/tabs-list:border-transparent group-data-[variant=line]/tabs-list:-mb-px group-data-[variant=line]/tabs-list:hover:text-text-primary group-data-[variant=line]/tabs-list:data-active:text-primary-600 group-data-[variant=line]/tabs-list:data-active:border-b-primary-500',
                className,
            )}
            {...props}
        />
    );
}

function TabsContent({ className, ...props }: React.ComponentProps<typeof TabsPrimitive.Content>) {
    return (
        <TabsPrimitive.Content
            data-slot="tabs-content"
            className={cn('flex-1 text-sm outline-none', className)}
            {...props}
        />
    );
}

export { Tabs, TabsList, TabsTrigger, TabsContent, tabsListVariants };
